// components/useCanvasDrawingToolController.ts
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernelStore, useKernelStoreApi } from '@/kernel/kernelStores';
import type { DrawingToolStyle, KernelHandle, KernelState, Layer, Workspace } from '#types/types';
import {
  DEFAULT_DRAWING_TEXT_FONT_FAMILY,
  createPencilDrawingSvg,
  createPolygonDrawingSvg,
  createShapeDrawingSvg,
  createTextDrawingSvg,
  DRAWING_LAYER_PARAM_KEY,
  getDrawingLayerPayload,
  type DrawingSvgResult,
} from '#lib/drawing-layer';
import { SVG_LAYER_PARAM_KEY } from '#lib/svg-layer';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import {
  isDrawingControlsTarget,
  isShapeDrawingKind,
  resolveDisplayedTextDrawingStyle,
  type PencilDrawingDraft,
  type PolygonDrawingDraft,
  type ShapeDrawingDraft,
  type ShapeDrawingKind,
  type TextDrawingDraft,
} from './CanvasDrawingToolOverlay';

type WorldPoint = { x: number; y: number };
type CanvasPointerEvent = React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>;

interface UseCanvasDrawingToolControllerParams {
  kernel: React.RefObject<KernelHandle | null>;
  projectId: string;
  screenToWorld: (screenX: number, screenY: number) => WorldPoint;
  findTopWorkspaceAtWorldPosition: (
    worldPos: WorldPoint,
    options?: { annotationsOnly?: boolean }
  ) => Workspace | null;
}

const MIN_DRAWING_DRAG_DISTANCE = 3;
const MIN_PENCIL_POINT_DISTANCE = 1.5;
const MAX_PENCIL_DOUBLE_CLICK_DISTANCE = 8;
const MAX_PENCIL_DOUBLE_CLICK_DELAY_MS = 650;
// Screen-space radius (px) within which clicking the first polygon vertex closes the shape. Converted
// to world units via screenToWorld so the snap target stays visually constant across zoom levels.
const POLYGON_CLOSE_SCREEN_DISTANCE = 14;

const resolveUniformShapeEnd = (
  start: WorldPoint,
  end: WorldPoint,
  kind: ShapeDrawingKind,
  forceUniform: boolean,
): WorldPoint => {
  if (
    !forceUniform ||
    (
      kind !== 'rectangle' &&
      kind !== 'ellipse' &&
      kind !== 'triangle' &&
      kind !== 'diamond' &&
      kind !== 'star' &&
      kind !== 'pentagon' &&
      kind !== 'hexagon'
    )
  ) {
    return end;
  }
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const size = Math.max(Math.abs(dx), Math.abs(dy));
  if (size <= 0) return end;
  const signX = dx < 0 ? -1 : 1;
  const signY = dy < 0 ? -1 : 1;
  return {
    x: start.x + signX * size,
    y: start.y + signY * size,
  };
};

const getShapeDrawingLabel = (kind: ShapeDrawingKind): string => {
  switch (kind) {
    case 'rectangle':
      return 'Rectangle';
    case 'ellipse':
      return 'Ellipse';
    case 'triangle':
      return 'Triangle';
    case 'diamond':
      return 'Diamond';
    case 'star':
      return 'Star';
    case 'pentagon':
      return 'Pentagon';
    case 'hexagon':
      return 'Hexagon';
    case 'arrow':
      return 'Arrow';
    case 'line':
      return 'Line';
  }
};

const resolveDrawingStyleFromState = (state: KernelState): DrawingToolStyle => ({
  strokeColor: state.drawingStrokeColor,
  strokeOpacity: state.drawingStrokeOpacity,
  fillColor: state.drawingFillColor,
  fillOpacity: state.drawingFillOpacity,
  fillEnabled: state.drawingFillEnabled,
  strokeWidth: state.drawingStrokeWidth,
  cornerRadius: state.drawingCornerRadius,
  fontSize: state.drawingFontSize,
  fontColor: state.drawingFontColor,
  fontOpacity: state.drawingFontOpacity,
  textBold: state.drawingTextBold,
  textItalic: state.drawingTextItalic,
  textUnderline: state.drawingTextUnderline,
  fontFamily: state.drawingFontFamily,
  fontWeight: state.drawingFontWeight,
  lineHeight: state.drawingLineHeight,
  letterSpacingPercent: state.drawingLetterSpacingPercent,
  textAlign: state.drawingTextAlign,
});

const appendPencilPoint = (
  points: WorldPoint[],
  point: WorldPoint,
): WorldPoint[] => {
  const lastPoint = points[points.length - 1];
  if (lastPoint && Math.hypot(point.x - lastPoint.x, point.y - lastPoint.y) < MIN_PENCIL_POINT_DISTANCE) {
    return points;
  }
  return [...points, point];
};

export const useCanvasDrawingToolController = ({
  kernel,
  projectId,
  screenToWorld,
  findTopWorkspaceAtWorldPosition,
}: UseCanvasDrawingToolControllerParams) => {
  const kernelStore = useKernelStoreApi();
  const activeTool = useKernelStore((state) => state.activeTool);
  const drawingToolKind = useKernelStore((state) => state.drawingToolKind);
  const drawingStrokeColor = useKernelStore((state) => state.drawingStrokeColor);
  const drawingStrokeOpacity = useKernelStore((state) => state.drawingStrokeOpacity);
  const drawingFillColor = useKernelStore((state) => state.drawingFillColor);
  const drawingFillOpacity = useKernelStore((state) => state.drawingFillOpacity);
  const drawingFillEnabled = useKernelStore((state) => state.drawingFillEnabled);
  const drawingStrokeWidth = useKernelStore((state) => state.drawingStrokeWidth);
  const drawingCornerRadius = useKernelStore((state) => state.drawingCornerRadius);
  const drawingFontSize = useKernelStore((state) => state.drawingFontSize);
  const drawingFontColor = useKernelStore((state) => state.drawingFontColor);
  const drawingFontOpacity = useKernelStore((state) => state.drawingFontOpacity);
  const drawingTextBold = useKernelStore((state) => state.drawingTextBold);
  const drawingTextItalic = useKernelStore((state) => state.drawingTextItalic);
  const drawingTextUnderline = useKernelStore((state) => state.drawingTextUnderline);
  const drawingFontFamily = useKernelStore((state) => state.drawingFontFamily);
  const drawingFontWeight = useKernelStore((state) => state.drawingFontWeight);
  const drawingLineHeight = useKernelStore((state) => state.drawingLineHeight);
  const drawingLetterSpacingPercent = useKernelStore((state) => state.drawingLetterSpacingPercent);
  const drawingTextAlign = useKernelStore((state) => state.drawingTextAlign);

  const shapeDrawingDraftRef = useRef<ShapeDrawingDraft | null>(null);
  const pencilDrawingDraftRef = useRef<PencilDrawingDraft | null>(null);
  const polygonDrawingDraftRef = useRef<PolygonDrawingDraft | null>(null);
  const pencilClickTrackerRef = useRef<{
    previous: { x: number; y: number; time: number } | null;
    current: { x: number; y: number; time: number } | null;
  }>({ previous: null, current: null });
  const textDrawingDraftRef = useRef<TextDrawingDraft | null>(null);
  const drawingControlsPointerDownAtRef = useRef(0);

  const [shapeDrawingDraft, setShapeDrawingDraft] = useState<ShapeDrawingDraft | null>(null);
  const [pencilDrawingDraft, setPencilDrawingDraft] = useState<PencilDrawingDraft | null>(null);
  const [polygonDrawingDraft, setPolygonDrawingDraft] = useState<PolygonDrawingDraft | null>(null);
  const [textDrawingDraft, setTextDrawingDraft] = useState<TextDrawingDraft | null>(null);
  const [drawingPanelRequestToken, setDrawingPanelRequestToken] = useState(0);

  const drawingStyle = useMemo<DrawingToolStyle>(() => ({
    strokeColor: drawingStrokeColor,
    strokeOpacity: drawingStrokeOpacity,
    fillColor: drawingFillColor,
    fillOpacity: drawingFillOpacity,
    fillEnabled: drawingFillEnabled,
    strokeWidth: drawingStrokeWidth,
    cornerRadius: drawingCornerRadius,
    fontSize: drawingFontSize,
    fontColor: drawingFontColor,
    fontOpacity: drawingFontOpacity,
    textBold: drawingTextBold,
    textItalic: drawingTextItalic,
    textUnderline: drawingTextUnderline,
    fontFamily: drawingFontFamily,
    fontWeight: drawingFontWeight,
    lineHeight: drawingLineHeight,
    letterSpacingPercent: drawingLetterSpacingPercent,
    textAlign: drawingTextAlign,
  }), [
    drawingCornerRadius,
    drawingFillColor,
    drawingFillEnabled,
    drawingFillOpacity,
    drawingFontColor,
    drawingFontFamily,
    drawingFontOpacity,
    drawingFontSize,
    drawingFontWeight,
    drawingLetterSpacingPercent,
    drawingLineHeight,
    drawingStrokeColor,
    drawingStrokeOpacity,
    drawingStrokeWidth,
    drawingTextAlign,
    drawingTextBold,
    drawingTextItalic,
    drawingTextUnderline,
  ]);

  const addDrawingLayerFromResult = useCallback((
    workspace: Workspace,
    result: DrawingSvgResult,
    name: string,
  ) => {
    if (!kernel.current) return null;
    const newLayer = kernel.current.addSvgLayer(workspace.id, {
      width: result.worldRect.w,
      height: result.worldRect.h,
    }, {
      name,
      rect: {
        x: result.worldRect.x - workspace.frameRect.x,
        y: result.worldRect.y - workspace.frameRect.y,
        w: result.worldRect.w,
        h: result.worldRect.h,
      },
      scale: 1,
      locked: false,
      source: 'drawing',
      type: 'svg',
      imageData: null,
      params: {
        [DRAWING_LAYER_PARAM_KEY]: result.payload,
        [SVG_LAYER_PARAM_KEY]: {
          svg: result.svg,
          width: result.worldRect.w,
          height: result.worldRect.h,
          viewBox: `0 0 ${result.worldRect.w} ${result.worldRect.h}`,
          source: 'drawing',
        },
      },
    });
    kernel.current.selectLayer(projectId, newLayer.id, false);
    kernel.current.invalidateWorkspaceVersionCache(workspace.id);
    kernel.current.logUserAction('Drawing Layer Created', {
      workspaceId: workspace.id,
      layerId: newLayer.id,
      kind: result.payload.kind,
    });
    return newLayer;
  }, [kernel, projectId]);

  const commitShapeDrawingDraft = useCallback((draft: ShapeDrawingDraft) => {
    if (!kernel.current) return;
    const workspace = kernel.current.getWorkspace(draft.workspaceId);
    if (!workspace) {
      kernel.current.setActiveTool(null);
      return;
    }
    const distance = Math.hypot(draft.current.x - draft.start.x, draft.current.y - draft.start.y);
    if (distance < MIN_DRAWING_DRAG_DISTANCE) {
      kernel.current.setActiveTool(null);
      return;
    }
    const result = createShapeDrawingSvg({
      kind: draft.kind,
      start: draft.start,
      end: draft.current,
      style: resolveDrawingStyleFromState(kernelStore.getState()),
    });
    addDrawingLayerFromResult(workspace, result, getShapeDrawingLabel(draft.kind));
    kernel.current.setActiveTool(null);
  }, [addDrawingLayerFromResult, kernel, kernelStore]);

  const commitPencilDrawingDraft = useCallback((draft: PencilDrawingDraft, options?: { keepTool?: boolean }) => {
    if (!kernel.current) return;
    const workspace = kernel.current.getWorkspace(draft.workspaceId);
    const uniquePoints = draft.points.filter((point, index, points) => {
      if (index === 0) return true;
      const previous = points[index - 1];
      return previous ? Math.hypot(point.x - previous.x, point.y - previous.y) >= MIN_PENCIL_POINT_DISTANCE : true;
    });
    if (!workspace || uniquePoints.length < 2) {
      if (!options?.keepTool) {
        kernel.current.setActiveTool(null);
      }
      return;
    }
    const result = createPencilDrawingSvg({
      points: uniquePoints,
      style: resolveDrawingStyleFromState(kernelStore.getState()),
    });
    addDrawingLayerFromResult(workspace, result, 'Pen');
    if (!options?.keepTool) {
      kernel.current.setActiveTool(null);
    }
  }, [addDrawingLayerFromResult, kernel, kernelStore]);

  // Convert the fixed screen-space close radius into world units at the current zoom by measuring how
  // far POLYGON_CLOSE_SCREEN_DISTANCE screen px maps to in world space. Falls back to the raw constant.
  const getPolygonCloseWorldDistance = useCallback((): number => {
    const origin = screenToWorld(0, 0);
    const offset = screenToWorld(POLYGON_CLOSE_SCREEN_DISTANCE, 0);
    const distance = Math.hypot(offset.x - origin.x, offset.y - origin.y);
    return Number.isFinite(distance) && distance > 0 ? distance : POLYGON_CLOSE_SCREEN_DISTANCE;
  }, [screenToWorld]);

  const commitPolygonDrawingDraft = useCallback((
    draft: PolygonDrawingDraft,
    options?: { keepTool?: boolean },
  ) => {
    if (!kernel.current) return;
    const workspace = kernel.current.getWorkspace(draft.workspaceId);
    if (!workspace || draft.points.length < 3) {
      if (!options?.keepTool) {
        kernel.current.setActiveTool(null);
      }
      return;
    }
    const result = createPolygonDrawingSvg({
      points: draft.points,
      style: resolveDrawingStyleFromState(kernelStore.getState()),
    });
    addDrawingLayerFromResult(workspace, result, 'Polygon');
    if (!options?.keepTool) {
      kernel.current.setActiveTool(null);
    }
  }, [addDrawingLayerFromResult, kernel, kernelStore]);

  const commitTextDrawingDraft = useCallback(() => {
    if (!kernel.current) return;
    const draft = textDrawingDraftRef.current;
    if (!draft) return;
    textDrawingDraftRef.current = null;
    const text = draft.value.trim();
    const workspace = kernel.current.getWorkspace(draft.workspaceId);
    setTextDrawingDraft(null);
    if (!workspace || !text) {
      kernel.current.setActiveTool(null);
      return;
    }
    const result = createTextDrawingSvg({
      text,
      anchor: draft.anchor,
      style: draft.style,
    });

    if (draft.editingLayerId) {
      const previousLayer = kernel.current.getLayerRegistry()[draft.editingLayerId];
      if (!previousLayer) {
        kernel.current.setActiveTool(null);
        return;
      }
      const updatedLayer: Layer = {
        ...previousLayer,
        imageData: null,
        type: 'svg',
        rect: {
          x: result.worldRect.x - workspace.frameRect.x,
          y: result.worldRect.y - workspace.frameRect.y,
          w: result.worldRect.w,
          h: result.worldRect.h,
        },
        scale: 1,
        source: 'drawing',
        params: {
          ...(previousLayer.params && typeof previousLayer.params === 'object' ? previousLayer.params : {}),
          [DRAWING_LAYER_PARAM_KEY]: result.payload,
          [SVG_LAYER_PARAM_KEY]: {
            svg: result.svg,
            width: result.worldRect.w,
            height: result.worldRect.h,
            viewBox: `0 0 ${result.worldRect.w} ${result.worldRect.h}`,
            source: 'drawing',
          },
        },
        isAltered: false,
        contentVersion: Date.now(),
      };

      kernel.current.executeAction({
        description: 'Edit drawing text',
        redo: () => {
          if (!kernel.current) return;
          kernel.current.setLayer(updatedLayer.id, updatedLayer);
          kernel.current.selectLayer(projectId, updatedLayer.id, false);
          kernel.current.invalidateWorkspaceVersionCache(workspace.id);
        },
        undo: () => {
          if (!kernel.current) return;
          kernel.current.setLayer(previousLayer.id, previousLayer);
          kernel.current.selectLayer(projectId, previousLayer.id, false);
          kernel.current.invalidateWorkspaceVersionCache(workspace.id);
        },
      });
      kernel.current.logUserAction('Drawing Text Edited', {
        workspaceId: workspace.id,
        layerId: updatedLayer.id,
      });
      kernel.current.setActiveTool(null);
      return;
    }

    addDrawingLayerFromResult(workspace, result, 'Text');
    kernel.current.setActiveTool(null);
  }, [addDrawingLayerFromResult, kernel, projectId]);

  useEffect(() => {
    if (activeTool !== 'draw') return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      if (
        !shapeDrawingDraftRef.current &&
        !pencilDrawingDraftRef.current &&
        !polygonDrawingDraftRef.current &&
        !textDrawingDraftRef.current
      ) return;
      event.preventDefault();
      shapeDrawingDraftRef.current = null;
      pencilDrawingDraftRef.current = null;
      polygonDrawingDraftRef.current = null;
      pencilClickTrackerRef.current = { previous: null, current: null };
      textDrawingDraftRef.current = null;
      setShapeDrawingDraft(null);
      setPencilDrawingDraft(null);
      setPolygonDrawingDraft(null);
      setTextDrawingDraft(null);
      kernel.current?.setActiveTool(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTool, kernel]);

  useEffect(() => {
    if (activeTool === 'draw') return;
    shapeDrawingDraftRef.current = null;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the drafts are React state mirrored in refs, and leaving the draw tool is what ends them: settling them elsewhere would change when a draft is committed
    setShapeDrawingDraft(null);
    if (pencilDrawingDraftRef.current) {
      const draft = pencilDrawingDraftRef.current;
      pencilDrawingDraftRef.current = null;
      pencilClickTrackerRef.current = { previous: null, current: null };
      setPencilDrawingDraft(null);
      if (draft.points.length >= 2) {
        commitPencilDrawingDraft(draft, { keepTool: true });
      }
    }
    if (polygonDrawingDraftRef.current) {
      const draft = polygonDrawingDraftRef.current;
      polygonDrawingDraftRef.current = null;
      setPolygonDrawingDraft(null);
      if (draft.points.length >= 3) {
        commitPolygonDrawingDraft(draft, { keepTool: true });
      }
    }
    if (textDrawingDraftRef.current) {
      commitTextDrawingDraft();
      return;
    }
    setTextDrawingDraft(null);
  }, [activeTool, commitPencilDrawingDraft, commitPolygonDrawingDraft, commitTextDrawingDraft]);

  useEffect(() => {
    if (drawingToolKind === 'pencil') return;
    const draft = pencilDrawingDraftRef.current;
    if (!draft) return;
    pencilDrawingDraftRef.current = null;
    pencilClickTrackerRef.current = { previous: null, current: null };
    setPencilDrawingDraft(null);
    if (draft.points.length >= 2) {
      commitPencilDrawingDraft(draft, { keepTool: true });
    }
  }, [commitPencilDrawingDraft, drawingToolKind]);

  useEffect(() => {
    if (drawingToolKind === 'polygon') return;
    const draft = polygonDrawingDraftRef.current;
    if (!draft) return;
    polygonDrawingDraftRef.current = null;
    setPolygonDrawingDraft(null);
    if (draft.points.length >= 3) {
      commitPolygonDrawingDraft(draft, { keepTool: true });
    }
  }, [commitPolygonDrawingDraft, drawingToolKind]);

  useEffect(() => {
    if (activeTool !== 'draw') return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      const draft = pencilDrawingDraftRef.current;
      if (!draft || draft.points.length < 2) return;
      event.preventDefault();
      pencilDrawingDraftRef.current = null;
      setPencilDrawingDraft(null);
      commitPencilDrawingDraft(draft);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTool, commitPencilDrawingDraft]);

  const handleTextDrawingBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (isDrawingControlsTarget(event.relatedTarget)) return;
    window.setTimeout(() => {
      if (Date.now() - drawingControlsPointerDownAtRef.current < 250) return;
      if (isDrawingControlsTarget(document.activeElement)) return;
      commitTextDrawingDraft();
    }, 0);
  }, [commitTextDrawingDraft]);

  useEffect(() => {
    if (!textDrawingDraft) return;
    const handleDocumentPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-drawing-text-input="true"]')) return;
      if (isDrawingControlsTarget(target)) {
        drawingControlsPointerDownAtRef.current = Date.now();
        return;
      }
      commitTextDrawingDraft();
    };
    document.addEventListener('mousedown', handleDocumentPointerDown, true);
    document.addEventListener('touchstart', handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handleDocumentPointerDown, true);
      document.removeEventListener('touchstart', handleDocumentPointerDown, true);
    };
  }, [commitTextDrawingDraft, textDrawingDraft]);

  const handleTextDraftChange = useCallback((value: string) => {
    setTextDrawingDraft((current) => {
      const next = current ? { ...current, value } : current;
      textDrawingDraftRef.current = next;
      return next;
    });
  }, []);

  const cancelTextDrawing = useCallback(() => {
    textDrawingDraftRef.current = null;
    setTextDrawingDraft(null);
    kernel.current?.setActiveTool(null);
  }, [kernel]);

  const handleDrawingPointerDown = useCallback((
    event: CanvasPointerEvent,
    clientX: number,
    clientY: number,
    isTouch: boolean,
    button: number,
  ): boolean => {
    if (!(isTouch || button === 0) || kernel.current?.getActiveTool() !== 'draw') {
      return false;
    }
    if (textDrawingDraftRef.current) {
      return true;
    }
    const worldPos = screenToWorld(clientX, clientY);
    const targetWorkspace = findTopWorkspaceAtWorldPosition(worldPos);
    if (!targetWorkspace) {
      return true;
    }

    if (targetWorkspace.id !== kernel.current?.getProjectUIWorkspaceId(projectId)) {
      kernel.current?.setProjectUIWorkspaceId(projectId, targetWorkspace.id);
      kernel.current?.logUserAction('Workspace Switched by Drawing Interaction', {
        target_workspace_id: targetWorkspace.id,
        reason: 'drawing_hit',
      });
    }

    const drawingState = kernelStore.getState();
    const currentToolKind = drawingState.drawingToolKind;

    if (currentToolKind === 'text') {
      const nextTextDraft: TextDrawingDraft = {
        workspaceId: targetWorkspace.id,
        anchor: worldPos,
        value: 'Text',
        style: resolveDrawingStyleFromState(drawingState),
      };
      textDrawingDraftRef.current = nextTextDraft;
      setTextDrawingDraft(nextTextDraft);
      return true;
    }

    if (currentToolKind === 'pencil') {
      if (!isTouch) {
        const currentClick = { x: clientX, y: clientY, time: Date.now() };
        pencilClickTrackerRef.current = {
          previous: pencilClickTrackerRef.current.current,
          current: currentClick,
        };
      }
      const existingDraft = pencilDrawingDraftRef.current;

      if (existingDraft && existingDraft.workspaceId === targetWorkspace.id && !existingDraft.isDragging) {
        const nextPoints = appendPencilPoint(existingDraft.points, worldPos);
        const nextDraft: PencilDrawingDraft = {
          ...existingDraft,
          points: nextPoints,
          dragStart: worldPos,
          isDragging: false,
          hasDragMoved: false,
        };

        pencilDrawingDraftRef.current = nextDraft;
        setPencilDrawingDraft(nextDraft);
        return true;
      }

      const nextPencilDraft: PencilDrawingDraft = {
        workspaceId: targetWorkspace.id,
        points: [worldPos],
        dragStart: worldPos,
        isDragging: false,
        hasDragMoved: false,
      };
      pencilDrawingDraftRef.current = nextPencilDraft;
      setPencilDrawingDraft(nextPencilDraft);
      return true;
    }

    if (currentToolKind === 'polygon') {
      const existingDraft = polygonDrawingDraftRef.current;
      if (existingDraft && existingDraft.workspaceId === targetWorkspace.id) {
        const first = existingDraft.points[0];
        if (existingDraft.points.length >= 3 && first) {
          const closeDistance = getPolygonCloseWorldDistance();
          if (Math.hypot(worldPos.x - first.x, worldPos.y - first.y) <= closeDistance) {
            polygonDrawingDraftRef.current = null;
            setPolygonDrawingDraft(null);
            commitPolygonDrawingDraft(existingDraft);
            return true;
          }
        }
        const nextDraft: PolygonDrawingDraft = {
          ...existingDraft,
          points: [...existingDraft.points, worldPos],
          cursor: worldPos,
          canClose: false,
        };
        polygonDrawingDraftRef.current = nextDraft;
        setPolygonDrawingDraft(nextDraft);
        return true;
      }

      const nextPolygonDraft: PolygonDrawingDraft = {
        workspaceId: targetWorkspace.id,
        points: [worldPos],
        cursor: worldPos,
        canClose: false,
      };
      polygonDrawingDraftRef.current = nextPolygonDraft;
      setPolygonDrawingDraft(nextPolygonDraft);
      return true;
    }

    if (!isShapeDrawingKind(currentToolKind)) {
      return true;
    }

    const nextDraft: ShapeDrawingDraft = {
      workspaceId: targetWorkspace.id,
      start: worldPos,
      current: resolveUniformShapeEnd(worldPos, worldPos, currentToolKind, event.shiftKey),
      kind: currentToolKind,
    };
    shapeDrawingDraftRef.current = nextDraft;
    setShapeDrawingDraft(nextDraft);
    return true;
  }, [
    commitPolygonDrawingDraft,
    findTopWorkspaceAtWorldPosition,
    getPolygonCloseWorldDistance,
    kernel,
    kernelStore,
    projectId,
    screenToWorld,
  ]);

  const handleDrawingPointerMove = useCallback((
    event: CanvasPointerEvent,
    clientX: number,
    clientY: number,
    isTouch: boolean,
  ): boolean => {
    if (shapeDrawingDraftRef.current) {
      const rawCurrent = screenToWorld(clientX, clientY);
      const nextDraft = {
        ...shapeDrawingDraftRef.current,
        current: resolveUniformShapeEnd(
          shapeDrawingDraftRef.current.start,
          rawCurrent,
          shapeDrawingDraftRef.current.kind,
          event.shiftKey,
        ),
      };
      shapeDrawingDraftRef.current = nextDraft;
      setShapeDrawingDraft(nextDraft);
      return true;
    }

    if (pencilDrawingDraftRef.current) {
      const isPrimaryPointerDown =
        isTouch ||
        ((event as React.MouseEvent<HTMLDivElement>).buttons & 1) === 1;
      if (!isPrimaryPointerDown) {
        return true;
      }
      const currentPoint = screenToWorld(clientX, clientY);
      const draft = pencilDrawingDraftRef.current;
      if (!draft.hasDragMoved && draft.points.length > 1) {
        return true;
      }
      const dragStart = draft.dragStart ?? draft.points[draft.points.length - 1] ?? currentPoint;
      const hasDragMoved =
        draft.hasDragMoved ||
        Math.hypot(currentPoint.x - dragStart.x, currentPoint.y - dragStart.y) >= MIN_DRAWING_DRAG_DISTANCE;
      const nextPoints = hasDragMoved
        ? appendPencilPoint(draft.points, currentPoint)
        : draft.points;
      const nextDraft: PencilDrawingDraft = {
        ...draft,
        points: nextPoints,
        isDragging: hasDragMoved,
        hasDragMoved,
      };
      pencilDrawingDraftRef.current = nextDraft;
      setPencilDrawingDraft(nextDraft);
      return true;
    }

    if (polygonDrawingDraftRef.current) {
      const draft = polygonDrawingDraftRef.current;
      const cursor = screenToWorld(clientX, clientY);
      const first = draft.points[0];
      const canClose =
        draft.points.length >= 3 &&
        Boolean(first) &&
        Math.hypot(cursor.x - (first?.x ?? 0), cursor.y - (first?.y ?? 0)) <= getPolygonCloseWorldDistance();
      const nextDraft: PolygonDrawingDraft = {
        ...draft,
        cursor,
        canClose,
      };
      polygonDrawingDraftRef.current = nextDraft;
      setPolygonDrawingDraft(nextDraft);
      return true;
    }

    return false;
  }, [getPolygonCloseWorldDistance, screenToWorld]);

  const handleDrawingPointerUp = useCallback((
    event: CanvasPointerEvent,
    clientX: number,
    clientY: number,
  ): boolean => {
    if (shapeDrawingDraftRef.current) {
      const rawCurrent = screenToWorld(clientX, clientY);
      const finalDraft = {
        ...shapeDrawingDraftRef.current,
        current: resolveUniformShapeEnd(
          shapeDrawingDraftRef.current.start,
          rawCurrent,
          shapeDrawingDraftRef.current.kind,
          event.shiftKey,
        ),
      };
      shapeDrawingDraftRef.current = null;
      setShapeDrawingDraft(null);
      commitShapeDrawingDraft(finalDraft);
      return true;
    }

    if (pencilDrawingDraftRef.current) {
      const draft = pencilDrawingDraftRef.current;
      if (draft.isDragging || draft.hasDragMoved) {
        const finalDraft: PencilDrawingDraft = {
          ...draft,
          points: appendPencilPoint(draft.points, screenToWorld(clientX, clientY)),
        };
        pencilDrawingDraftRef.current = null;
        pencilClickTrackerRef.current = { previous: null, current: null };
        setPencilDrawingDraft(null);
        commitPencilDrawingDraft(finalDraft);
      }
      return true;
    }

    return false;
  }, [commitPencilDrawingDraft, commitShapeDrawingDraft, screenToWorld]);

  const handleDrawingDoubleClick = useCallback((event: React.MouseEvent<HTMLDivElement>): boolean => {
    if (kernel.current?.getActiveTool() !== 'draw') {
      return false;
    }

    const currentToolKind = kernelStore.getState().drawingToolKind;

    if (currentToolKind === 'polygon') {
      const polygonDraft = polygonDrawingDraftRef.current;
      if (!polygonDraft) return false;
      // Each preceding click already placed a vertex; double-click just finalizes the shape. The two
      // clicks of the double-click can duplicate the last vertex, so drop a trailing near-duplicate.
      let points = polygonDraft.points;
      const lastPoint = points[points.length - 1];
      const prevPoint = points[points.length - 2];
      if (
        lastPoint &&
        prevPoint &&
        Math.hypot(lastPoint.x - prevPoint.x, lastPoint.y - prevPoint.y) < MIN_PENCIL_POINT_DISTANCE
      ) {
        points = points.slice(0, -1);
      }
      // Only close on a valid polygon; with < 3 vertices keep the draft alive so the user can keep
      // placing points (Escape cancels).
      if (points.length < 3) {
        return true;
      }
      event.preventDefault();
      event.stopPropagation();
      polygonDrawingDraftRef.current = null;
      setPolygonDrawingDraft(null);
      commitPolygonDrawingDraft({ ...polygonDraft, points });
      return true;
    }

    if (currentToolKind !== 'pencil') {
      return false;
    }
    const draft = pencilDrawingDraftRef.current;
    if (!draft) return false;
    const clickTracker = pencilClickTrackerRef.current;
    const previousClick = clickTracker.previous;
    const currentClick = clickTracker.current;
    const isConfirmedDoubleClick =
      Boolean(previousClick && currentClick) &&
      Math.hypot(
        (currentClick?.x ?? 0) - (previousClick?.x ?? 0),
        (currentClick?.y ?? 0) - (previousClick?.y ?? 0),
      ) <= MAX_PENCIL_DOUBLE_CLICK_DISTANCE &&
      (currentClick?.time ?? 0) - (previousClick?.time ?? 0) <= MAX_PENCIL_DOUBLE_CLICK_DELAY_MS;
    if (!isConfirmedDoubleClick) return false;

    event.preventDefault();
    event.stopPropagation();

    const worldPos = screenToWorld(event.clientX, event.clientY);
    const targetWorkspace = findTopWorkspaceAtWorldPosition(worldPos);
    const finalDraft: PencilDrawingDraft = {
      ...draft,
      points: targetWorkspace?.id === draft.workspaceId
        ? appendPencilPoint(draft.points, worldPos)
        : draft.points,
      dragStart: worldPos,
      isDragging: false,
      hasDragMoved: false,
    };

    pencilDrawingDraftRef.current = null;
    pencilClickTrackerRef.current = { previous: null, current: null };
    setPencilDrawingDraft(null);
    commitPencilDrawingDraft(finalDraft);
    return true;
  }, [
    commitPencilDrawingDraft,
    commitPolygonDrawingDraft,
    findTopWorkspaceAtWorldPosition,
    kernel,
    kernelStore,
    screenToWorld,
  ]);

  const startEditingDrawingTextLayer = useCallback((layerId: string): boolean => {
    if (!kernel.current) return false;
    const layer = kernel.current.getLayerRegistry()[layerId];
    if (!layer || layer.source !== 'drawing') return false;
    const payload = getDrawingLayerPayload(layer.params);
    if (!payload || payload.kind !== 'text') return false;
    const workspace = kernel.current.getWorkspace(layer.layerWorkspaceId);
    if (!workspace) return false;
    const displayedStyle = resolveDisplayedTextDrawingStyle(layer, payload);
    const nextDraft: TextDrawingDraft = {
      workspaceId: workspace.id,
      anchor: {
        x: workspace.frameRect.x + layer.rect.x,
        y: workspace.frameRect.y + layer.rect.y,
      },
      value: payload.text ?? '',
      editingLayerId: layer.id,
      style: displayedStyle,
    };
    textDrawingDraftRef.current = nextDraft;
    setTextDrawingDraft(nextDraft);
    kernel.current.selectLayer(projectId, layer.id, false);
    kernel.current.setDrawingToolKind('text');
    kernel.current.setDrawingFontSize(displayedStyle.fontSize);
    kernel.current.setDrawingFontColor(displayedStyle.fontColor);
    kernel.current.setDrawingFontOpacity(displayedStyle.fontOpacity ?? 1);
    kernel.current.setDrawingTextBold(displayedStyle.textBold);
    kernel.current.setDrawingTextItalic(displayedStyle.textItalic);
    kernel.current.setDrawingTextUnderline(displayedStyle.textUnderline);
    kernel.current.setDrawingFontFamily(displayedStyle.fontFamily ?? DEFAULT_DRAWING_TEXT_FONT_FAMILY);
    kernel.current.setDrawingFontWeight(displayedStyle.fontWeight ?? (displayedStyle.textBold ? 700 : 400));
    kernel.current.setDrawingLineHeight(displayedStyle.lineHeight ?? 1.22);
    kernel.current.setDrawingLetterSpacingPercent(displayedStyle.letterSpacingPercent ?? 0);
    kernel.current.setDrawingTextAlign(displayedStyle.textAlign ?? 'left');
    kernel.current.setActiveTool('draw');
    setDrawingPanelRequestToken((current) => current + 1);
    kernel.current.logUserAction('Drawing Text Edit Started', {
      workspaceId: workspace.id,
      layerId: layer.id,
    });
    return true;
  }, [kernel, projectId]);

  const rerenderTextDrawingLayerAtCurrentSize = useCallback((layer: Layer): Layer | null => {
    if (!kernel.current || layer.source !== 'drawing') return null;
    const payload = getDrawingLayerPayload(layer.params);
    if (!payload || payload.kind !== 'text') return null;
    const workspace = kernel.current.getWorkspace(layer.layerWorkspaceId);
    const text = typeof payload.text === 'string' ? payload.text : '';
    if (!workspace || text.trim().length === 0) return null;
    const style = resolveDisplayedTextDrawingStyle(layer, payload);
    const result = createTextDrawingSvg({
      text,
      anchor: {
        x: workspace.frameRect.x + layer.rect.x,
        y: workspace.frameRect.y + layer.rect.y,
      },
      style,
    });
    return {
      ...layer,
      imageData: null,
      type: 'svg',
      rect: {
        x: result.worldRect.x - workspace.frameRect.x,
        y: result.worldRect.y - workspace.frameRect.y,
        w: result.worldRect.w,
        h: result.worldRect.h,
      },
      scale: 1,
      params: {
        ...(layer.params && typeof layer.params === 'object' ? layer.params : {}),
        [DRAWING_LAYER_PARAM_KEY]: result.payload,
        [SVG_LAYER_PARAM_KEY]: {
          svg: result.svg,
          width: result.worldRect.w,
          height: result.worldRect.h,
          viewBox: `0 0 ${result.worldRect.w} ${result.worldRect.h}`,
          source: 'drawing',
        },
      },
      isAltered: false,
      contentVersion: Date.now(),
    };
  }, [kernel]);

  return {
    shapeDrawingDraft,
    pencilDrawingDraft,
    polygonDrawingDraft,
    textDrawingDraft,
    drawingStyle,
    drawingPanelRequestToken,
    handleDrawingPointerDown,
    handleDrawingPointerMove,
    handleDrawingPointerUp,
    handleDrawingDoubleClick,
    handleTextDrawingBlur,
    handleTextDraftChange,
    cancelTextDrawing,
    commitTextDrawingDraft,
    startEditingDrawingTextLayer,
    rerenderTextDrawingLayerAtCurrentSize,
  };
};
